import React from 'react';
import styled from 'styled-components';

import { useService } from '@xstate/react';

import { productBuilderMachine } from '../../../../context/ProductBuilder/machine';
import { getBaseProduct } from '../../../../helpers/getBaseProduct';
import { getProductType } from '../../../../helpers/getProductType';
import { getAllowedValue } from '../../../../helpers/measurements';
import {
  getMaxHeight,
  getMaxWidth,
  getMinWidth,
  getRollerDayNightMaxWidth,
} from '../../../../helpers/metafields';
import { useDebounce } from '../../../../hooks/useDebounce';


type Props = {
  maxWidth: number;
  maxHeight: number;
  arrowWidth: number | string;
  arrowHeight: number | string;
  bottom: number | string;
  top: number | string;
  arrowRight: number | string;
  windowFrameHeight: number;
  windowFrameWidth: number;
};

const Wrapper = styled.div<{
  arrowWidth: number | string;
  arrowHeight: number | string;
  bottom: number | string;
  top: number | string;
  arrowRight: number | string;
  windowFrameHeight: number;
  windowFrameWidth: number;
}>`
  width: 100%;
  height: 100%;
  font-size: 10px;
  color: #db8462;

  > .row {
    display: flex;
    justify-content: space-between;
    flex-direction: row;
    height: 10px;
    align-items: center;
    justify-content: center;
    position: absolute;
    margin-top: 0px;

    @media (min-width: 768px) {
      top:50px;
      margin-top: 0px;
    }

     @media (min-width: 991px) {
   top:60px;
   margin-top: 0px;
  }
   @media (min-width: 2000px) {
   top:45px;
   margin-top: 0px;
  }
   @media (max-width: 767px) {
   top:30px;
   margin-top: 0px;
  }

    ${({ arrowWidth }) => {
    return arrowWidth !== '' ? `width: ${arrowWidth}px;` : '';
  }};
    ${({ arrowRight }) => {
    return arrowRight !== '' ? `left: ${arrowRight}px;` : '';
  }};
    ${({ arrowWidth, windowFrameWidth }) => {
    const translateWidth =
      typeof arrowWidth === 'number'
        ? `${arrowWidth + windowFrameWidth / 2}px`
        : '100%';
    return arrowWidth !== ''
      ? `transform: translate(-${translateWidth}, 0);`
      : '';
  }};
    ${({ top }) => {
    const rowTop = top !== 'auto' && typeof top === 'number' ? top - 24 : '';
    return top !== '' ? `top: ${rowTop}px;` : '';
  }};
    ${({ bottom, top }) => {
    return bottom !== '' && top === '' ? `bottom: ${bottom}px;` : 'auto';
  }};
  }

  .row-marker {
    width: 1px;
    background: #db8462;
    height: 8px;
  }

  .row-line {
    height: 1px;
    width: 40%;
    background: #db8462;
  }

  .row-measurement {
    margin: 0 auto;
    text-align: center;
    width: auto;
  }

  > .col {
    z-index: 4;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    position: absolute;

    @media (min-width: 1451px) {
      top: 160px;
    }
    @media (min-width: 1950px) {
      top: 110px;
    }
    @media (max-width: 991px) {
      top: 110px;
    }
    @media (max-width: 991px) {
      top: 110px;
    }
    @media (max-width: 991px) {
      top: 110px;
    }
    @media (max-width: 767px) {
      top: 30px;
      margin-top: 0px;
    } 

    ${({ arrowHeight }) => {
    return arrowHeight !== '' ? `height: ${arrowHeight}px;` : '';
  }};
    ${({ arrowRight }) => {
    return arrowRight !== '' ? `left: ${arrowRight}px;` : '';
  }};
    ${({ top, windowFrameHeight }) => {
    const arrowTopMeasurement =
      typeof top === 'number' && windowFrameHeight
        ? top + windowFrameHeight / 2
        : top;
    return arrowTopMeasurement !== '' ? `top: ${arrowTopMeasurement}px;` : '';
  }};
    ${({ bottom, top }) => {
    return bottom !== '' && top === '' ? `bottom: ${bottom}px;` : 'auto';
  }};
  }

  .col-marker {
    width: 8px;
    background: #db8462;
    height: 1px;
  }

  .col-line {
    height: 41%;
    width: 1px;
    background: #db8462;
  }

  .col-measurement {
    margin: auto 0;
    width: auto;
    transform: rotate(90deg);
  }

  @media (min-width: 1199px) {
    font-size: calc(var(--bs-h4) - 1px);
    
    .col-line {
       height: 32%;
    }
    .row-line {
      width: 36%;
    }
  }
  
  @media (max-width: 767px) {
    .col-line {
      height: 36%;
    } 
  }
  @media (max-width: 374px) {
    font-size: 8px;
  }
`;

export const BuilderMeasurement = ({
  // maxWidth,
  // maxHeight,
  arrowWidth,
  arrowHeight,
  top,
  bottom,
  arrowRight,
  windowFrameHeight,
  windowFrameWidth,
}: Props) => {
  const [state] = useService(productBuilderMachine);
  const {
    custom: { measurements },
  } = state.context;

  const [width, height] = measurements
    ? (measurements as string).split('x').map(value => parseInt(value))
    : ['', ''];

  const baseProduct = getBaseProduct(state.context)
    ? getBaseProduct(state.context)
    : state.context.products[0];

  const minWidth = getMinWidth(baseProduct);
  let maxWidth = getMaxWidth(baseProduct);

  // update max width for day/night roller
  const productSubType = getProductType(state.context);
  const rollerDayNightMaxWidth = getRollerDayNightMaxWidth(baseProduct);
  if (
    productSubType &&
    productSubType.includes('Roller') &&
    window.location.pathname.includes('roller-day-night')
  ) {
    maxWidth =
      maxWidth > rollerDayNightMaxWidth ? rollerDayNightMaxWidth : maxWidth;
  }

  const maxHeight = getMaxHeight(
    baseProduct,
    getAllowedValue(width, minWidth, maxWidth),
  );

  if ((width && height) || (maxWidth && maxHeight)) {
    return (
      <Wrapper
        arrowWidth={arrowWidth}
        arrowHeight={arrowHeight}
        bottom={bottom}
        top={top}
        arrowRight={arrowRight}
        windowFrameWidth={windowFrameWidth}
        windowFrameHeight={windowFrameHeight}
      >
        <div className="row">
          <span className="row-marker"></span>
          <span className="row-line"></span>
          <span className="row-measurement">{width || maxWidth}mm</span>
          <span className="row-line"></span>
          <span className="row-marker"></span>
        </div>
        <div className="col">
          <span className="col-marker"></span>
          <span className="col-line"></span>
          <span className="col-measurement">{height || maxHeight}mm</span>
          <span className="col-line"></span>
          <span className="col-marker"></span>
        </div>
      </Wrapper>
    );
  }
  return null;
};
